import { useEffect, useState } from "react";
import UploadProgressRing from "./UploadProgressRing";

const isImageFile = (file) => String(file?.type || "").startsWith("image/");
const isPdfFile = (file) => {
  const fileType = String(file?.type || "").toLowerCase();
  return fileType === "application/pdf" || /\.pdf$/i.test(file?.name || "");
};

const getFileExtension = (file) => {
  const fileName = String(file?.name || "");
  const match = fileName.match(/\.([^.]+)$/);
  return match?.[1]?.toUpperCase() || "FILE";
};

function FileFallback({ label, sublabel = "پیش‌نمایش فایل" }) {
  return (
    <div className="size-full flex flex-col items-center justify-center gap-1 bg-gradient-to-br from-[#eef3ff] to-[#dfe9ff] px-2 text-center dark:from-[#203244] dark:to-[#172433]">
      <div className="rounded-full bg-white/80 px-3 py-1 text-[10px] font-semibold tracking-[0.24em] text-[#335c95] dark:bg-[#0f1720]/70 dark:text-[#a9c7ff]">
        {label}
      </div>
      <p className="text-[11px] leading-5 text-[#365177] dark:text-[#d9e8ff]">
        {sublabel}
      </p>
    </div>
  );
}

function ImageUploadPreviewCard({
  file,
  onRemove,
  uploading = false,
  uploadProgress = 0,
  disabled = false,
  showFileName = true,
  cardClassName = "",
  previewClassName = "",
}) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!(file instanceof File)) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <div className={cardClassName || "w-24 sm:w-28"}>
      <div
        className={`relative ${
          previewClassName || "aspect-square"
        } overflow-hidden rounded-xl border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark`}
      >
        {previewUrl && isImageFile(file) ? (
          <img
            src={previewUrl}
            alt={file?.name || "upload-preview"}
            className="size-full object-cover"
          />
        ) : previewUrl && isPdfFile(file) ? (
          <object
            data={previewUrl}
            type="application/pdf"
            aria-label={file?.name || "pdf-preview"}
            className="size-full"
          >
            <FileFallback label="PDF" />
          </object>
        ) : (
          <FileFallback label={getFileExtension(file)} />
        )}

        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/55 backdrop-blur-[1px]">
            <UploadProgressRing progress={uploadProgress} />
          </div>
        )}

        {!disabled && !uploading && typeof onRemove === "function" && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
            aria-label={`حذف ${file?.name || "تصویر"}`}
            className="absolute left-1.5 top-1.5 flex size-6 items-center justify-center rounded-full bg-black/65 text-sm leading-none text-white transition-opacity hocus:opacity-80"
          >
            ×
          </button>
        )}
      </div>

      {showFileName && (
        <p className="mt-1.5 break-all text-center text-[11px] leading-5 whitespace-nowrap overflow-auto text-zi-text-light dark:text-zi-text-dark">
          {file?.name || "-"}
        </p>
      )}
    </div>
  );
}

export default function ImageUploadPreviewGrid({
  files = [],
  onRemove,
  uploading = false,
  uploadProgress = 0,
  disabled = false,
  className = "",
  wrap = true,
  showFileName = true,
  cardClassName = "",
  previewClassName = "",
}) {
  if (!Array.isArray(files) || files.length === 0) return null;

  return (
    <div
      className={`w-full flex ${
        wrap ? "flex-wrap" : "flex-nowrap"
      } gap-3 ${className}`.trim()}
    >
      {files.map((file, index) => (
        <ImageUploadPreviewCard
          key={`${file?.name || "file"}-${file?.lastModified || index}-${index}`}
          file={file}
          onRemove={() => onRemove?.(index)}
          uploading={uploading}
          uploadProgress={uploadProgress}
          disabled={disabled}
          showFileName={showFileName}
          cardClassName={cardClassName}
          previewClassName={previewClassName}
        />
      ))}
    </div>
  );
}
